import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Button } from "@fieldbook/ui/button"
import { Eye, LeafIcon, TagIcon, EyeIcon, ArrowLeft } from "lucide-react"

export const Route = createFileRoute(
  "/_private/projects/$id/observations/new/",
)({
  component: NewObservationSelector,
})

type ObservationType = {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  route: string
}

function NewObservationSelector() {
  const { id: projectId } = Route.useParams()
  const { siteId } = Route.useSearch()
  const navigate = useNavigate()

  const observationTypes: ObservationType[] = [
    {
      id: "species",
      title: "Species Observation",
      description: "Record a sighting of a known or new species",
      icon: <LeafIcon className="size-8 text-green-600" />,
      route: "/projects/$id/observations/new/species",
    },
    {
      id: "newCollection",
      title: "New Collection",
      description: "Create a new collection record for a specimen",
      icon: <TagIcon className="size-8 text-blue-600" />,
      route: "/projects/$id/observations/new/collection",
    },
    {
      id: "existingCollection",
      title: "Existing Collection",
      description: "Record an observation linked to an existing collection",
      icon: <EyeIcon className="size-8 text-purple-600" />,
      route: "/projects/$id/observations/new/existing",
    },
  ]

  const handleSelectType = (type: ObservationType) => {
    navigate({
      to: type.route,
      params: { id: projectId },
      search: siteId ? { siteId } : undefined,
    })
  }

  const handleBack = () => {
    if (siteId) {
      navigate({
        to: "/projects/$id/data/sites/$siteId",
        params: { id: projectId, siteId },
        search: { tab: "observations" },
      })
    } else {
      navigate({
        to: "/projects/$id/data/observations",
        params: { id: projectId },
      })
    }
  }

  return (
    <div className="bg-background absolute inset-0 z-20 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b p-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="shrink-0"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Eye className="size-6" />
          <h1 className="text-xl font-semibold">New Observation</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-muted-foreground mb-6">
          Choose the type of observation you want to record
        </p>

        <div className="space-y-3">
          {observationTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleSelectType(type)}
              className={`hover:border-primary hover:bg-primary/5 w-full cursor-pointer rounded-lg border p-4 text-left transition-colors`}
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 rounded-lg bg-gray-100 p-3">
                  {type.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{type.title}</h3>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {type.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Cancel button */}
      <div className="border-t p-4">
        <Button
          variant="secondary"
          onClick={handleBack}
          className="h-12 w-full text-lg"
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
